import { SendResetPasswordEmail } from '@/apis/users/requests.ts'
import { static_errors } from '@/common/constants'
import { Button } from '@/app/common/components/button/Button'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Input } from '@/app/common/components/input/Input'
import { EyeIcon, EyeSlashIcon } from '@/common/icons/general.tsx'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { RootState } from '@/storage/store.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { ChangePassword } from '@/apis/users/requests'

const LoginMethodSchema = z.object({
  email: z.string().email('Invalid email address'),
  current_password: z.string().min(1, {
    message: 'Current Password is required',
  }),
  new_password: z
    .string()
    .min(8, { message: 'Password must be at least 8 characters long' })
    .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/, {
      message:
        'Password must contain at least one uppercase letter, one lowercase letter, and one special character.',
    }),
  new_password_confirmation: z.string().min(8, {
    message: 'Confirm Password must be at least 8 characters long',
  }),
})

const ApiErrorBodySchema = z.object({
  details: z.record(z.string()).nullable(),
})

type LoginMethodFormProps = z.infer<typeof LoginMethodSchema>

interface BasicSettingsProps {
  id: number
  email: string
  phone: string
  idScroll?: string
}
const BasicSettings: React.FC<BasicSettingsProps> = ({
  id,
  email,
  idScroll,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<LoginMethodFormProps>({
    defaultValues: {
      email: email,
      new_password: '',
      current_password: '',
      new_password_confirmation: '',
    },
    resolver: zodResolver(LoginMethodSchema),
  })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const user = useSelector((state: RootState) => state.auth.user)
  const { fireAlert } = useAppAlert()
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const isProfileOwner = user?.id == id

  const onSubmit = async (formData: LoginMethodFormProps) => {
    if (!id) return
    const { error } = await ChangePassword({
      user_id: id,
      payload: {
        new_password: formData.new_password ?? '',
        new_password_confirmation: formData.new_password_confirmation ?? '',
        current_password: formData.current_password ?? '',
      },
    })
    if (error) {
      const parsed = ApiErrorBodySchema.safeParse(error)
      const message =
        parsed.success && parsed.data.details
          ? Object.values(parsed.data.details)[0]
          : (error.message ?? static_errors.unexpected_contact_support)
      return fireAlert({
        variant: 'error',
        message,
      })
    } else {
      await fireAlert({
        variant: 'success',
        message: 'Password changed successfully',
      }).then(async () => {
        window.location.reload()
      })
    }
  }

  const handleResetPassword = async () => {
    setIsLoading(true)
    const { error, data } = await SendResetPasswordEmail(email)
    setIsLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
    } else {
      await fireAlert({
        variant: 'success',
        message: data.message,
      })
    }
  }

  const password = useWatch({ control, name: 'new_password' })
  const confirmPassword = useWatch({
    control,
    name: 'new_password_confirmation',
  })

  return (
    <>
      <Card className="mt-4" id={idScroll}>
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="text-left text-md font-semibold text-gray-900">
            Log in Method
          </div>
        </CardHeader>
        <CardContent className="p-0 lg:p-0 card-body grid gap-5">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-4 py-6 sm:p-8">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  User ID Email
                </label>
                <Input
                  type="email"
                  {...register('email')}
                  placeholder="Enter your email"
                  readOnly
                />
                <FormErrorMessage message={errors.email?.message} />
              </div>

              {isProfileOwner && (
                <>
                  <div className="mb-4">
                    <label
                      htmlFor="current_password"
                      className="block font-medium mb-1"
                    >
                      Current Password
                    </label>
                    <div className="relative flex">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        {...register('current_password')}
                        placeholder="Enter current password"
                      />
                      <div
                        className="absolute right-0 flex items-center h-full mr-2"
                        onClick={() => {
                          setShowCurrentPassword(!showCurrentPassword)
                        }}
                      >
                        {showCurrentPassword ? <EyeIcon /> : <EyeSlashIcon />}
                      </div>
                    </div>
                    <FormErrorMessage
                      message={errors.current_password?.message}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="new_password"
                      className="block font-medium mb-1"
                    >
                      New Password
                    </label>
                    <div className="relative flex">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        {...register('new_password')}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                      <div
                        className="absolute right-0 flex items-center h-full mr-2"
                        onClick={() => {
                          setShowNewPassword(!showNewPassword)
                        }}
                      >
                        {showNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
                      </div>
                    </div>
                    <FormErrorMessage message={errors.new_password?.message} />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="password"
                      className="block font-medium mb-1"
                    >
                      Confirm Password
                    </label>
                    <div className="relative flex">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...register('new_password_confirmation')}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                      <div
                        className="absolute right-0 flex items-center h-full mr-2"
                        onClick={() => {
                          setShowConfirmPassword(!showConfirmPassword)
                        }}
                      >
                        {showConfirmPassword ? <EyeIcon /> : <EyeSlashIcon />}
                      </div>
                    </div>
                    <FormErrorMessage
                      message={
                        confirmPassword && password !== confirmPassword
                          ? 'Passwords do not match'
                          : errors.new_password_confirmation?.message
                            ? errors.new_password_confirmation.message
                            : ''
                      }
                    />
                  </div>
                </>
              )}

              <div className="flex justify-end">
                {!isProfileOwner && (
                  <>
                    {isLoading ? (
                      <CircleLoader size="sm" className="mr-1" />
                    ) : (
                      <Button
                        variant={'soft'}
                        className="mr-1"
                        onClick={handleResetPassword}
                      >
                        Reset Password
                      </Button>
                    )}
                  </>
                )}

                <Button type="submit">Save Changes</Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  )
}

export default BasicSettings
